import cn from 'classnames'
import { useCallback, useState } from 'react'
import { flushSync } from 'react-dom'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'
import { useTranslations } from 'next-intl'
import { useStageCanContinue } from '@booking/hooks/useStageCanContinue'
import { useBookingState } from '@booking/state/booking.context'

type ButtonProps = {
  onContinue: () => void | Promise<void>
  className: string
  shake?: boolean
}

export default function ContinueButton({ onContinue, className, shake }: ButtonProps) {
  const t = useTranslations('booking')
  const canContinue = useStageCanContinue()
  const { loading } = useBookingState()
  const [submitting, setSubmitting] = useState(false)

  const handleClick = useCallback(async () => {
    flushSync(() => setSubmitting(true))
    try {
      await onContinue()
      // Success → navigation is in progress, component will unmount.
      // Don't reset — button stays disabled until the new stage renders.
    } catch {
      // Error → we're staying on this stage, re-enable so the user can retry.
      setSubmitting(false)
    }
  }, [onContinue])

  if (!onContinue) {
    return <></>
  }

  return (
    <button
      type="button"
      className={cn(shared.button, styles.button, shake && styles.shake, className)}
      onClick={handleClick}
      disabled={!canContinue || Boolean(loading) || submitting}
      data-id="continue"
    >
      {t('continue')}
    </button>
  )
}
